// DataAnalyticsPage.jsx
import React, { useEffect } from 'react';
import { Row, Col, Button, Table } from 'reactstrap';
import { Link } from 'react-router-dom';
import xsimage from '../../assets/images/strategyxs.png'


const DataAnalyticsPage = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
        // Scroll animation observer
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.style.animation = 'fadeInUp 0.8s ease-out forwards';
                }
            });
        }, { threshold: 0.1 });

        document.querySelectorAll('[data-animated]').forEach(el => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    const analyticsStats = [
        { number: '87%', label: 'of companies use data analytics to gain competitive advantage' },
        { number: '3.1x', label: 'higher revenue growth for data-driven organizations' },
        { number: '45%', label: 'improvement in decision-making speed with analytics insights' },
        { number: '60%', label: 'reduction in operational costs through data optimization' }
    ];

    const analyticsApproaches = [
        {
            icon: 'fas fa-chart-line',
            title: 'Analytics Strategy & Planning',
            desc: 'Comprehensive KPI definition, measurement frameworks, and data strategy aligned with business goals to ensure meaningful insights that drive results.'
        },
        {
            icon: 'fas fa-cogs',
            title: 'Implementation & Tracking Setup',
            desc: 'Expert configuration of analytics tools including Google Analytics 4, with proper tagging, event tracking, and cross-platform measurement integration.'
        },
        {
            icon: 'fas fa-database',
            title: 'Data Collection & Processing',
            desc: 'Centralized and automated data collection across platforms, creating clean, structured datasets ready for analysis and insight generation.'
        },
        {
            icon: 'fas fa-brain',
            title: 'Insight Generation & Reporting',
            desc: 'Advanced data analysis to uncover patterns, trends, and actionable insights with custom dashboards and executive-ready reporting.'
        },
        {
            icon: 'fas fa-rocket',
            title: 'Continuous Optimization & Insight Activation',
            desc: 'Ongoing performance tracking, trend analysis, and data-driven optimization to continuously improve digital effectiveness and ROI.'
        },
        {
            icon: 'fas fa-tools',
            title: 'Advanced Analytics Tools Implementation',
            desc: 'Deployment of heatmapping, session-recording, and BI solutions for comprehensive user behavior visualization and business intelligence.'
        }
    ];

    const benefits = [
        ['Better Decision-Making', 'Make informed choices based on real data rather than assumptions'],
        ['Increased Operational Efficiency', 'Identify optimization opportunities and streamline business processes'],
        ['Clear Performance Visibility', 'Understand exactly what drives results and where improvements are needed'],
        ['Scalable Insights Framework', 'Build analytics infrastructure that grows with your business needs'],
        ['Stronger Competitive Advantage', 'Leverage data insights to outperform competitors and capture market share']
    ];

    const targetBusinesses = [
        'Growing digital businesses needing clear ROI visibility and performance measurement',
        'Marketing teams requiring accurate campaign tracking and optimization insights',
        'Product and UX teams seeking user behavior analysis and experience improvements',
        'Executive stakeholders driving strategic decisions with data-backed intelligence',
        'Organizations looking to transform data into actionable growth opportunities',
        'Companies preparing for digital transformation or market expansion'
    ];

    const analyticsQuestions = [
        'What key metrics truly matter for your business success?',
        'How can you connect user behavior to business outcomes?',
        'What insights are you missing from your current data setup?',
        'How can analytics optimize your marketing spend and ROI?',
        'What emerging data trends could impact your competitive position?'
    ];

    return (
        <div className="strategy sv-page">
            {/* Decorative Image - Top of Page */}
            <div className="sv-decorative-image-container">

            </div>
            <div className='xs-sv-page'>
                <img src={xsimage} />
            </div>
            {/* Page Title Section */}
            <div className="sv-page-title-section">
                <div className="container">
                    <h1>
                        <span className="sv-gradient-text">Data Analytics</span> Services
                    </h1>
                    <p>Turn data into actionable insights and strategic growth opportunities</p>
                </div>
            </div>

            {/* Main Content */}
            <div className="container-fluid sv-content-wrapper">
                <div className="sv-main-content">
                    {/* Hero Content Section */}
                    <section className="sv-hero-section" data-animated>
                        <Row className="align-items-center">
                            <Col lg={8}>
                                <p className="sv-lead">
                                    In a world overflowing with information, businesses need more than raw numbers — they need clarity, insight, and direction.
                                </p>
                                <p className="sv-description">
                                    Reachtix's <strong>Data Analytics services</strong> help brands transform data into a powerful decision-making engine that drives performance, uncovers opportunities, and fuels sustainable growth.
                                </p>
                                <p className="sv-description">
                                    Rather than letting data sit unused, we help you leverage it to make smarter decisions across every part of your business, turning guesswork into informed action.
                                </p>
                                <div className="sv-cta-buttons">
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='dark'
                                    >
                                        <i className="fas fa-rocket mr-2"></i>
                                        Start Analytics Strategy
                                    </Button>
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='dark'
                                    >
                                        <i className="fas fa-comments mr-2"></i>
                                        Get Analytics Consultation
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Stats Section */}
                    <section className="sv-stats-section">
                        <h2 className="sv-section-title" data-animated>
                            Why <span className="sv-text-gradient">Data Analytics Matters</span>
                        </h2>
                        <Row className="sv-stats-grid">
                            {analyticsStats.map((stat, index) => (
                                <Col lg={6} md={6} key={index} className="mb-4" data-animated>
                                    <div className="sv-stat-card">
                                        <div className="sv-stat-number">{stat.number}</div>
                                        <div className="sv-stat-label">{stat.label}</div>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </section>

                    {/* What Is Data Analytics Section */}
                    <section className="sv-explanation-section" data-animated>
                        <Row>
                            <Col lg={8}>
                                <h2 className="sv-section-title">What Is Data Analytics?</h2>
                                <p className="sv-section-description">
                                    Data analytics is the process of collecting, analysing and interpreting data from digital channels — including web, app and business performance — to understand what's happening, why it's happening, and what actions deliver value next.
                                </p>
                                <p className="sv-section-description">
                                    With robust analytics in place, you can understand user interactions, identify growth opportunities, make informed decisions, and drive continuous improvement with measurable insights.
                                </p>
                                <div className="sv-questions-grid">
                                    <h4>Key Analytics Questions We Answer:</h4>
                                    {analyticsQuestions.map((question, index) => (
                                        <div key={index} className="sv-question-item">
                                            <i className="fas fa-question-circle"></i>
                                            {question}
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Why Analytics Section */}
                    <section className="sv-benefits-overview" data-animated>
                        <Row>
                            <Col xl={12}>
                                <h2 className="sv-section-title">Why Data Analytics Drives Success</h2>
                                <p className="sv-section-description">
                                    Analytics transforms raw data into strategic assets, enabling businesses to move from intuition-based decisions to evidence-driven growth.
                                </p>
                                <div className="sv-benefits-cards">
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-bullseye"></i>
                                        <h5>Informed Decision-Making</h5>
                                        <p>Make strategic choices based on real data rather than assumptions</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-search"></i>
                                        <h5>Performance Visibility</h5>
                                        <p>Understand what drives results and identify optimization opportunities</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-chart-bar"></i>
                                        <h5>ROI Optimization</h5>
                                        <p>Maximize marketing spend effectiveness and business returns</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-trophy"></i>
                                        <h5>Competitive Advantage</h5>
                                        <p>Leverage data insights before competitors to capture market share</p>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Our Analytics Approach Section */}
                    <section className="sv-approach-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Our Data Analytics Services at Reachtix</h2>
                                <p className="sv-section-description">
                                    We deliver a full spectrum of analytics solutions — from foundational tracking to high-impact insight generation — tailored to your specific business needs.
                                </p>

                                <div className="sv-approaches-grid">
                                    {analyticsApproaches.map((item, index) => (
                                        <div key={index} className="sv-approach-card">
                                            <div className="sv-approach-icon">
                                                <i className={item.icon}></i>
                                            </div>
                                            <div className="sv-approach-content">
                                                <h4>{item.title}</h4>
                                                <p>{item.desc}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Benefits Section */}
                    <section className="sv-benefits-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="mt-5 sv-section-title">Benefits of Data Analytics With Reachtix</h2>
                                <div className="table-responsive">
                                    <Table borderless className="sv-benefits-table">
                                        <thead>
                                            <tr>
                                                <th>Business Outcome</th>
                                                <th>Strategic Impact</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {benefits.map(([outcome, impact], index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <i className="fas fa-check-circle"></i>
                                                        <strong>{outcome}</strong>
                                                    </td>
                                                    <td>{impact}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Who Should Invest Section */}
                    <section className="sv-target-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Who Should Invest in Data Analytics?</h2>
                                <p className="sv-section-description">
                                    Data analytics delivers maximum value for organizations seeking clarity, optimization, and data-driven competitive advantage:
                                </p>
                                <div className="sv-target-list">
                                    {targetBusinesses.map((business, index) => (
                                        <div key={index} className="sv-target-item">
                                            <i className="fas fa-chevron-right"></i>
                                            {business}
                                        </div>
                                    ))}
                                </div>
                                <p className="sv-section-description mt-4">
                                    Whether you're launching new initiatives, optimizing campaigns, or scaling operations, analytics provides the clarity needed to make confident, impactful decisions.
                                </p>
                            </Col>
                        </Row>
                    </section>

                    {/* Final CTA Section */}
                    <section className="sv-main-cta" data-animated>
                        <Row>
                            <Col>
                                <h2>Ready to Turn Data Into Growth?</h2>
                                <p className="sv-cta-description">
                                    Analytics can be the catalyst that elevates your business performance — if it's done right. Reachtix's <strong>Data Analytics services</strong> give you the clarity, insight, and action plans you need to succeed in a data-driven world.
                                </p>
                                <p className="sv-cta-description">
                                    Our approach transforms your data into a tangible business advantage that informs every decision you make.
                                </p>
                                <div className="sv-cta-buttons">
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='primary'
                                    >
                                        <i className="fas fa-chart-line mr-2"></i>
                                        Start Analytics Strategy
                                    </Button>
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='light'
                                    >
                                        <i className="fas fa-search mr-2"></i>
                                        Get Free Analytics Audit
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default DataAnalyticsPage;